import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import toast, { Toaster } from 'react-hot-toast';
import swal from 'sweetalert';
import ArthaGateway from 'service/ArthaGateway';
import { EmployeeAllocation, ProductionEntry, ProductionLine, ProductionPlant, Product, WorkSession, Session, ProductVariant } from 'service';
import { Loader } from '../Widgets';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';

const getToday = () => new Date().toISOString().split('T')[0];

const getEmployeeImage = (employee?: { profileImage?: string } | null): string => employee?.profileImage || '';
const formatQuantity = (value: number) => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const formatValue = (value: number) => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const ProductionEntryPage = () => {
    const userData = useSelector((state: RootState) => state.appdata);
    const [userSession, setUserSession] = useState<Session>();
    const [lines, setLines] = useState<ProductionLine[]>([]);
    const [plants, setPlants] = useState<ProductionPlant[]>([]);
    const [sessions, setSessions] = useState<WorkSession[]>([]);
    const [productVariants, setProductVariants] = useState<ProductVariant[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [allocation, setAllocation] = useState<EmployeeAllocation | null>(null);
    const [productionEntries, setProductionEntries] = useState<ProductionEntry[]>([]);

    const [selectedDate, setSelectedDate] = useState<string>(getToday());
    const [selectedSessionId, setSelectedSessionId] = useState<number>(0);
    const [selectedPlant, setSelectedPlant] = useState<ProductionPlant | null>(null);
    const [selectedLine, setSelectedLine] = useState<ProductionLine | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSavingEntry, setIsSavingEntry] = useState<boolean>(false);
    const [deletingEntryId, setDeletingEntryId] = useState<number>(0);
    const [isEntryModalOpen, setIsEntryModalOpen] = useState<boolean>(false);
    const [editingEntry, setEditingEntry] = useState<ProductionEntry | null>(null);
    const [selectedVariantName, setSelectedVariantName] = useState<string>('');
    const [selectedVariantValue1, setSelectedVariantValue1] = useState<string>('');
    const [selectedProductId, setSelectedProductId] = useState<number>(0);
    const [entryQuantity, setEntryQuantity] = useState<number>(0);

    
    useEffect(() => {
        if (userData.profile) {
            setUserSession(userData.profile.profile);
        }
    }, [userData]);

    useEffect(() => {
        let mounted = true;

        const initialize = async () => {
            setIsLoading(true);
            try {
                await loadProductionPlants();
                await loadProductionLines();
                await loadWorkSessions();
                await loadProductVariants();
                await loadProducts();
            }
            finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        initialize();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!selectedSessionId || !selectedPlant || !selectedLine) {
            return;
        }

        setAllocation(null);
        setProductionEntries([]);

        ArthaGateway.getEmployeeAllocations(selectedDate, selectedSessionId, selectedPlant.id, selectedLine.id)
            .then((data) => setAllocation(data || null))
            .catch((error) => console.log(error));

        ArthaGateway.getProductionEntries(selectedDate, selectedSessionId, selectedLine.id)
            .then((data) => setProductionEntries(data || []))
            .catch((error) => console.log(error));
    }, [selectedDate, selectedSessionId, selectedPlant, selectedLine]);

    useEffect(() => {
        if (!selectedPlant && plants.length > 0) {
            setSelectedPlant(plants.find((plant) => plant.id === 1) || plants[0]);
        }
    }, [plants, selectedPlant]);

    useEffect(() => {
        if (!selectedSessionId && sessions.length > 0) {
            const currentHour = new Date().getHours();
            const preferredSessionId = currentHour < 13 ? 1 : 2;
            const preferredSession = sessions.find((session) => session.id === preferredSessionId);
            setSelectedSessionId(preferredSession?.id || sessions[0].id);
        }
    }, [sessions, selectedSessionId]);

    useEffect(() => {
        if (lines.length === 0) {
            return;
        }

        setPlants((currentPlants) => currentPlants.map((plant) => ({
            ...plant,
            lines: lines.filter((line) => line.plantId === plant.id),
        })));
    }, [lines]);

    useEffect(() => {
        if (!selectedPlant) {
            return;
        }

        if (!selectedPlant.lines || selectedPlant.lines.length === 0) {
            setSelectedLine(null);
            return;
        }

        if (!selectedLine || !selectedPlant.lines.find((line) => line.id === selectedLine.id)) {
            setSelectedLine(selectedPlant.lines[0]);
        }
    }, [selectedPlant]);

    const loadProductionPlants = async (): Promise<ProductionPlant[]> => {
        const plantData = localStorage.getItem('plants');
        if (plantData) {
            const cachedPlants = JSON.parse(plantData) as ProductionPlant[];
            setPlants(cachedPlants);
            return cachedPlants;
        }

        try {
            const data = await ArthaGateway.getProductionPlants();
            const fetchedPlants = (data || []).filter((plant: ProductionPlant) => plant.isActive === true);
            setPlants(fetchedPlants);
            localStorage.setItem('plants', JSON.stringify(fetchedPlants));
            return fetchedPlants;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const loadProductionLines = async (): Promise<ProductionLine[]> => {
        const lineData = localStorage.getItem('lines');
        if (lineData) {
            const cachedLines = JSON.parse(lineData) as ProductionLine[];
            setLines(cachedLines);
            return cachedLines;
        }

        try {
            const data = await ArthaGateway.getProductLines();
            const activeLines = (data || []).filter((line: ProductionLine) => line.isActive);
            setLines(activeLines);
            localStorage.setItem('lines', JSON.stringify(activeLines));
            return activeLines;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const loadWorkSessions = async (): Promise<WorkSession[]> => {
        const sessionData = localStorage.getItem('sessions');
        if (sessionData) {
            const cachedSessions = JSON.parse(sessionData) as WorkSession[];
            setSessions(cachedSessions);
            return cachedSessions;
        }

        try {
            const data = await ArthaGateway.getWorkSessions();
            const fetchedSessions = data || [];
            setSessions(fetchedSessions);
            localStorage.setItem('sessions', JSON.stringify(fetchedSessions));
            return fetchedSessions;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const loadProductVariants = async (): Promise<ProductVariant[]> => {
        const variantData = localStorage.getItem('productVariants');
        if (variantData) {
            const cachedVariants = JSON.parse(variantData) as ProductVariant[];
            setProductVariants(cachedVariants);
            return cachedVariants;
        }
        try {
            const data = await ArthaGateway.getProductVariants();
            const fetchedVariants = (data || []) as ProductVariant[];
            fetchedVariants.forEach((variant: ProductVariant) => {
                if (variant.values) {
                    const arrayString = variant.values.replace(/{|}/g, '');
                    if (arrayString) {
                        variant.valueList = JSON.parse(arrayString);
                    }
                }
            });
            setProductVariants(fetchedVariants);
            localStorage.setItem('productVariants', JSON.stringify(fetchedVariants));
            return fetchedVariants;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const loadProducts = async (): Promise<Product[]> => {
        const productData = localStorage.getItem('products');
        if (productData) {
            const cachedProducts = JSON.parse(productData) as Product[];
            setProducts(cachedProducts);
            return cachedProducts;
        }
        try {
            const data = await ArthaGateway.getProducts();
            const fetchedProducts = data.entries || [];
            setProducts(fetchedProducts);
            localStorage.setItem('products', JSON.stringify(fetchedProducts));
            return fetchedProducts;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const getLineAllocation = (lineId: number) => allocation?.lines?.find((line) => line.id === lineId);

    const openEntryModal = () => {
        if (!selectedLine) {
            toast.error('Please select a production line first.');
            return;
        }

        const defaultVariant = productVariants[0];
        const defaultVariantName = defaultVariant?.name || '';
        const defaultVariantValue = defaultVariant?.valueList?.[0] || '';

        setSelectedVariantName(defaultVariantName);
        setSelectedVariantValue1(defaultVariantValue);
        setSelectedProductId(0);
        setEntryQuantity(0);
        setEditingEntry(null);
        setIsEntryModalOpen(true);
    };

    const openUpdateModal = (entry: ProductionEntry) => {
        const product = products.find((item) => item.id === entry.productId);
        if (!product) {
            toast.error('Selected product not found.');
            return;
        }

        const productGroupName = ((product as Product & { groupName?: string }).groupName || product.variantName1 || '') as string;
        const productVariantValue1 = product.variantValue1 || '';
        const boxQty = entry.packages > 0
            ? entry.packages
            : (product.itemsPerPackage ? Math.floor(entry.quantity / product.itemsPerPackage) : 0);

        setSelectedVariantName(productGroupName);
        setSelectedVariantValue1(productVariantValue1);
        setSelectedProductId(product.id);
        setEntryQuantity(boxQty);
        setEditingEntry(entry);
        setIsEntryModalOpen(true);
    };

    const closeEntryModal = () => {
        if (isSavingEntry) {
            return;
        }

        setIsEntryModalOpen(false);
        setSelectedVariantName('');
        setSelectedVariantValue1('');
        setSelectedProductId(0);
        setEntryQuantity(0);
        setEditingEntry(null);
        setDeletingEntryId(0);
    };

    const saveProductionEntry = async () => {
        if (!selectedPlant || !selectedLine || !selectedSessionId) {
            toast.error('Please select plant, line and session first.');
            return;
        }

        if (!selectedProductId) {
            toast.error('Please select a product.');
            return;
        }

        if (!entryQuantity || entryQuantity <= 0) {
            toast.error('Please enter a valid quantity.');
            return;
        }

        setIsSavingEntry(true);
        try {
            const product = products.find((item) => item.id === selectedProductId);
            if(!product) {
                toast.error('Selected product not found.');
                return;
            }
            const response = await ArthaGateway.saveProductionEntry({
                id: editingEntry?.id || 0,
                date: selectedDate,
                sessionId: selectedSessionId,
                lineId: selectedLine.id,
                productId: selectedProductId,
                quantity: entryQuantity * product.itemsPerPackage,
                weight: product?.weight || 0,
                itemsPerPackage: product?.itemsPerPackage || 0,
                packages: entryQuantity,
                createdBy: userSession?.user?.id || 0,
                createdOn: new Date().toISOString(),
            });
            if(!response) {
                toast.error('Unable to save production entry.');
                return;
            }
            setProductionEntries(response || []);
            toast.success(editingEntry ? 'Production entry updated successfully.' : 'Production entry saved successfully.');
            closeEntryModal();
        }
        catch (error) {
            console.log(error);
            toast.error('Unable to save production entry.');
        }
        finally {
            setIsSavingEntry(false);
        }
    };

    const deleteProductionEntry = async () => {
        if (!selectedLine || !selectedSessionId) {
            toast.error('Please select line and session first.');
            return;
        }

        if (!editingEntry) {
            return;
        }

        const shouldDelete = await swal({
            title: 'Delete ' + (editingEntry.productName || 'production entry') + '?',
            text: 'This will remove the selected production entry.',
            icon: 'warning',
            buttons: ['Cancel', 'Yes, delete'],
            dangerMode: true,
        });

        if (!shouldDelete) {
            return;
        }

        try {
            setDeletingEntryId(editingEntry.id);
            await ArthaGateway.deleteProductionEntry(selectedDate, selectedSessionId, selectedLine.id, editingEntry.productId);
            const refreshedEntries = await ArthaGateway.getProductionEntries(selectedDate, selectedSessionId, selectedLine.id);
            setProductionEntries(refreshedEntries || []);
            toast.success('Production entry deleted successfully.');
            closeEntryModal();
        }
        catch (error) {
            console.log(error);
            toast.error('Unable to delete production entry.');
        }
        finally {
            setDeletingEntryId(0);
        }
    };

    const selectedLineAllocation = selectedLine ? getLineAllocation(selectedLine.id) : null;
    const selectedLineEmployees = selectedLineAllocation?.employees || [];
    const selectedLineEntries = productionEntries || [];
    const selectedVariant = productVariants.find((variant) => variant.name === selectedVariantName);
    const variantValueOptions = selectedVariant?.valueList || [];
    const filteredProducts = products.filter((product) => {
        const productGroupName = (product as Product & { groupName?: string }).groupName || product.variantName1;
        return product.variantValue1 === selectedVariantValue1 && productGroupName === selectedVariantName;
    });
    const selectedProduct = filteredProducts.find((product) => product.id === selectedProductId) || null;
    const quantityInPieces = selectedProduct ? entryQuantity * (selectedProduct.itemsPerPackage || 0) : 0;
    const totalWeight = selectedProduct ? quantityInPieces * (selectedProduct.weight || 0) : 0;
    const isUpdateMode = editingEntry !== null;
    const canEditOldEntries = (new Date(selectedDate) >= new Date(getToday()) || (userSession?.user.roleName === 'Admin'));

    useEffect(() => {
        if (!isEntryModalOpen) {
            return;
        }

        const nextVariant = productVariants.find((variant) => variant.name === selectedVariantName);
        const nextValueOptions = nextVariant?.valueList || [];

        if (!nextValueOptions.includes(selectedVariantValue1)) {
            setSelectedVariantValue1(nextValueOptions[0] || '');
        }
    }, [selectedVariantName, selectedVariantValue1, productVariants, isEntryModalOpen]);

    useEffect(() => {
        if (!isEntryModalOpen) {
            return;
        }

        if (!filteredProducts.find((product) => product.id === selectedProductId)) {
            setSelectedProductId(filteredProducts[0]?.id || 0);
        }
    }, [filteredProducts, selectedProductId, isEntryModalOpen]);

    return (
        <div className="page page-work-allocation">
            {isLoading && <Loader />}

            {!isLoading && (
                <>
                    <div><Toaster /></div>
                    <div className="row mb-4">
                        <h1>Production Entry</h1>
                    </div>

                    <div className="row mb-1">
                        <div className="col-lg-4 col-md-12 mb-3">
                            <label className="form-label mb-0">Production Plant</label>
                            <select className="form-select" value={selectedPlant?.id || 0} onChange={(event) => setSelectedPlant(plants.find((plant) => plant.id === Number(event.target.value)) || null)}>
                                {plants.map((plant) => (
                                    <option key={plant.id} value={plant.id}>{plant.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-lg-4 col-md-12 mb-3">
                            <label className="form-label mb-0">Production Line</label>
                            <select className="form-select" value={selectedLine?.id || 0} onChange={(event) => setSelectedLine(selectedPlant?.lines?.find((line) => line.id === Number(event.target.value)) || null)}>
                                {selectedPlant?.lines?.map((line) => (
                                    <option key={line.id} value={line.id}>{line.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-lg-4 col-6 mb-3">
                            <label className="form-label mb-0">Date</label>
                            <input className="form-control" type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
                        </div>
                        <div className="col-lg-4 col-6 mb-3">
                            <label className="form-label mb-0">Session</label>
                            <select className="form-select" value={selectedSessionId} onChange={(event) => setSelectedSessionId(parseInt(event.target.value))}>
                                {sessions.map((session) => (
                                    <option key={session.id} value={session.id}>{session.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedPlant && selectedLine && (
                        <div className="row">
                            <div className="col-12 mb-4">
                                <div className="card card-item w-100">
                                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="fw-bold">Employees</div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-dark">{selectedLineEmployees.length} assigned</span>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        {selectedLineEmployees.length > 0 ? (
                                            <div className="d-flex flex-wrap gap-2">
                                                {selectedLineEmployees.map((employee) => {
                                                    const employeeImage = getEmployeeImage(employee);
                                                    return (
                                                        <div className="border rounded px-2 py-2 d-flex align-items-center w-100" key={`${selectedLine.id}-allocated-${employee.id}`}>
                                                            {employeeImage && (
                                                                <img
                                                                    src={employeeImage}
                                                                    alt={employee.name || 'Employee'}
                                                                    className="rounded-circle me-2"
                                                                    style={{ width: 36, height: 36, objectFit: 'cover' }}
                                                                />
                                                            )}
                                                            <span className="me-2 flex-grow-1">{employee.name || employee.id}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="desc">No employees allocated</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 mb-4">
                                <div className="card card-item w-100">
                                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="fw-bold">Production</div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {canEditOldEntries && (
                                                <button className="btn btn-outline-dark btn-sm" type="button" onClick={openEntryModal} disabled={selectedLineEmployees.length === 0}>
                                                    Add
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="card-body p-2">
                                        {selectedLineEntries.length > 0 ? (
                                            <div className="d-flex flex-column gap-2">
                                                {selectedLineEntries.map((entry) => (
                                                    <div
                                                        className="border rounded px-2 py-2"
                                                        key={entry.id}
                                                        onClick={() => {
                                                            if (!canEditOldEntries) {
                                                                toast.error('You are not allowed to edit production entries for past dates.');
                                                                return;
                                                            }
                                                            openUpdateModal(entry);
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                        style={{ cursor: 'pointer' }}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter' || event.key === ' ') {
                                                                event.preventDefault();
                                                                openUpdateModal(entry);
                                                            }
                                                        }}
                                                    >
                                                        <div className="fw-bold w-100 text-truncate" title={entry.productName}>{entry.productName}</div>

                                                        <div className="d-flex justify-content-between align-items-start mt-2 gap-2">
                                                            <div className="flex-grow-1">
                                                                <div className="small text-secondary">
                                                                    Quantity: <span className="fw-semibold">{formatQuantity(entry.quantity)}</span> {entry.unit || ''} ({formatQuantity(entry.packages)} box)
                                                                </div>
                                                                {entry.weight > 0 && (
                                                                    <div className="text-secondary small">Weight: <span className="fw-semibold text-dark">{formatValue(entry.weight)}</span> KG</div>
                                                                )}
                                                            </div>

                                                            <div className="text-center border rounded bg-light px-2 py-1" style={{ minWidth: 84, minHeight: 50 }}>
                                                                <div className="fw-bold" style={{ fontSize: '1.45rem', lineHeight: 1.05 }}>{formatQuantity(entry.packages)}</div>
                                                                {entry.quantity > 0 && (
                                                                    <div className="small text-secondary mt-1" style={{ lineHeight: 1.05 }}>
                                                                        <b>{formatQuantity(entry.quantity)}</b> {entry.unit || ''}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="desc">No production entry made</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedPlant && (!selectedPlant.lines || selectedPlant.lines.length === 0) && (
                        <div className="row">
                            <div className="col-12">
                                <div className="desc">No production lines</div>
                            </div>
                        </div>
                    )}

                    {isEntryModalOpen && (
                        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
                            <div className="bg-white rounded shadow p-3 d-flex flex-column" style={{ width: '95%', maxWidth: 560, overflow: 'hidden' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-3">
                                    <div>
                                        <div className="fw-bold">{isUpdateMode ? 'Update Production Entry' : 'Add Production Entry'}</div>
                                        <div className="desc">{selectedLine?.name} | {selectedDate}</div>
                                    </div>
                                    <button className="btn btn-sm btn-danger" type="button" onClick={closeEntryModal} disabled={isSavingEntry}>
                                        Close
                                    </button>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Group</label>
                                    <select className="form-select" value={selectedVariantName} onChange={(event) => setSelectedVariantName(event.target.value)} disabled={isSavingEntry || isUpdateMode}>
                                        {productVariants.map((variant) => (
                                            <option key={variant.id} value={variant.name}>{variant.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Package</label>
                                    <select className="form-select" value={selectedVariantValue1} onChange={(event) => setSelectedVariantValue1(event.target.value)} disabled={isSavingEntry || !selectedVariantName || isUpdateMode}>
                                        {variantValueOptions.map((variantValue) => (
                                            <option key={variantValue} value={variantValue}>{variantValue}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Product</label>
                                    <select className="form-select" value={selectedProductId} onChange={(event) => setSelectedProductId(Number(event.target.value))} disabled={isSavingEntry || !selectedVariantValue1 || isUpdateMode}>
                                        {filteredProducts.map((product) => (
                                            <option key={product.id} value={product.id}>{product.name}</option>
                                        ))}
                                    </select>
                                    {filteredProducts.length === 0 &&
                                        <div className="desc mt-2">No product available for selected variant and value.</div>
                                    }
                                </div>

                                {selectedProductId > 0 && (
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Quantity (boxe/s)</label>
                                        <input className="form-control" type="number" min={1} value={entryQuantity || ''} onChange={(event) => setEntryQuantity(Number(event.target.value))} disabled={isSavingEntry} />
                                    </div>
                                )}

                                {selectedProductId > 0 && entryQuantity > 0 && (
                                    <div className="mb-3">
                                        <div className="desc">Quantity in pieces: <span className="fw-bold">{quantityInPieces}</span></div>
                                        <div className="desc">Weight: <span className="fw-bold">{totalWeight.toFixed(2)}</span> KG</div>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between mt-3 border-top pt-3">
                                    <div>
                                        {isUpdateMode && (
                                            <button
                                                className="btn btn-outline-danger"
                                                type="button"
                                                onClick={deleteProductionEntry}
                                                disabled={isSavingEntry || deletingEntryId === (editingEntry?.id || 0)}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                    <button className="btn btn-dark" type="button" onClick={saveProductionEntry} disabled={isSavingEntry || !selectedProductId || entryQuantity <= 0 || !selectedVariantName || !selectedVariantValue1}>
                                        {isSavingEntry ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};